import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { webAppConfig } from '../../config/appConfig';

type LandingSectionId = 'home' | 'how' | 'features';

type HeaderProps = {
  className: string;
  brandHref: string;
  logoSrc?: string;
  brandText?: string;
  brandClassName: string;
  logoClassName: string;
  brandTextClassName?: string;
  variant?: 'default' | 'landing';
  rightContent?: React.ReactNode;
};

export default function Header({
  className,
  brandHref,
  logoSrc = webAppConfig.brand.logoPath,
  brandText = webAppConfig.brand.name,
  brandClassName,
  logoClassName,
  brandTextClassName,
  variant = 'default',
  rightContent,
}: Readonly<HeaderProps>) {
  const [theme, setTheme] = useState<'light' | 'dark'>('light');
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (variant !== 'landing') return;
    const stored = localStorage.getItem(webAppConfig.storageKeys.theme);
    const nextTheme = stored === 'dark' ? 'dark' : 'light';
    setTheme(nextTheme);
    document.documentElement.dataset.theme = nextTheme;
  }, [variant]);

  useEffect(() => {
    if (variant !== 'landing') return;
    document.documentElement.dataset.theme = theme;
    localStorage.setItem(webAppConfig.storageKeys.theme, theme);
  }, [theme, variant]);

  const scrollToSection = (id: LandingSectionId) => {
    const target = document.getElementById(id);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    const homeWithHash = `${webAppConfig.routes.home}#${id}`;
    if (location.pathname !== webAppConfig.routes.home || location.hash !== `#${id}`) {
      navigate(homeWithHash);
      return;
    }

    navigate(webAppConfig.routes.home);
  };

  return (
    <header className={className}>
      <Link to={brandHref} className={brandClassName} aria-label={`${webAppConfig.brand.name} home`}>
        <img 
          src={logoSrc}
          alt={`${webAppConfig.brand.name} logo`}
          className={logoClassName}
          width={38}
          height={38}
          loading="eager"
          decoding="async"
          style={{ 
            backgroundColor: 'transparent',
            border: 'none',
            objectFit: 'contain',
            display: 'block'
          }}
        />
        <span className={brandTextClassName ?? ''}>{brandText}</span>
      </Link>
      {variant === 'landing' ? (
        <nav className="landing-nav" aria-label="Primary">
          <button type="button" className="landing-nav-link" onClick={() => scrollToSection('home')}>Home</button>
          <button type="button" className="landing-nav-link" onClick={() => scrollToSection('how')}>How it works</button>
          <button type="button" className="landing-nav-link" onClick={() => scrollToSection('features')}>Features</button>
          <button
            type="button"
            className="landing-theme-toggle"
            onClick={() => setTheme((prev) => (prev === 'light' ? 'dark' : 'light'))}
            aria-label="Toggle dark and light theme"
            title="Toggle dark and light theme"
          >
            {theme === 'light' ? '🌙' : '☀️'}
          </button>
          <div className="landing-nav-actions">
            <Link to={webAppConfig.routes.app} className="landing-nav-link">Login</Link>
            <Link to={webAppConfig.routes.app} className="landing-nav-cta">Get Started</Link>
          </div>
        </nav>
      ) : rightContent}
    </header>
  );
}
